import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import logo from '../assets/logo.png';
import { LOCAL_STORAGE_KEYS } from '../constants/Constants';
import logout_axios_client from '../services/logout_url';
import './Styles/Header.css';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(() => localStorage.getItem(LOCAL_STORAGE_KEYS.isLoggedIn) === 'true');
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    setAccountMenuOpen(false);
    try {
      await logout_axios_client.post('logout');
    } finally {
      localStorage.clear();
      setIsLoggedIn(false);
      navigate('/login');
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setAccountMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on navigation and prevent body scroll
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header className="site-header">
        <div className="header-content">
          <div className="brand" onClick={() => navigate('/')} role="button">
            <img src={logo} alt="Hameruha Logo" className="logo" />
            <span className="company-title">Hameruha</span>
          </div>

          <nav className="desktop-nav">
            <NavLink to="/" className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')} end>Home</NavLink>
            <NavLink to="/videos" className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}>Videos</NavLink>
            <NavLink to="/about" className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}>About Us</NavLink>
            <NavLink to="/contact" className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}>Contact Us</NavLink>
            <a href="https://120seconds.successtradingplc.com" target="_blank" rel="noopener noreferrer" className="nav-link game-link">120 Game</a>
          </nav>

          <div className="header-actions">
            {isLoggedIn ? (
              <div className="account" ref={menuRef}>
                <button className="avatar-btn" aria-label="Account menu" onClick={() => setAccountMenuOpen(v => !v)}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                </button>
                {accountMenuOpen && (
                  <div className="menu" role="menu">
                    <button className="menu-item" role="menuitem" onClick={() => { setAccountMenuOpen(false); navigate('/settings'); }}>Settings</button>
                    <button className="menu-item danger" role="menuitem" onClick={handleLogout}>Logout</button>
                  </div>
                )}
              </div>
            ) : (
              <div className="auth-actions">
                <button className="btn ghost danger" onClick={() => navigate('/login')}>Sign in</button>
                <button className="btn primary danger" onClick={() => navigate('/register')}>Register</button>
              </div>
            )}
          </div>

          <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(true)} aria-label="Open menu">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>
          </button>
        </div>
      </header>

      {/* Mobile Drawer */}
      {mobileMenuOpen && (
        <div className="mobile-nav-overlay" onClick={() => setMobileMenuOpen(false)}>
          <div className="mobile-nav-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <span className="drawer-title">Menu</span>
              <button className="drawer-close-btn" onClick={() => setMobileMenuOpen(false)} aria-label="Close menu">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            <nav className="drawer-nav">
              <NavLink to="/" className="drawer-link" end>Home</NavLink>
              <NavLink to="/videos" className="drawer-link">Videos</NavLink>
              <NavLink to="/about" className="drawer-link">About Us</NavLink>
              <NavLink to="/contact" className="drawer-link">Contact Us</NavLink>
              <a href="https://120seconds.successtradingplc.com" target="_blank" rel="noopener noreferrer" className="drawer-link game-link">120 Game</a>
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;